import { Landmark, Building2, ShieldCheck, FileDown, ArrowRight, Target } from "lucide-react";
import { generateFederalPitchPacket } from "@/lib/federalPitchGenerator";
import SamGovBadge from "@/components/site/SamGovBadge";
import { useDeployment } from "@/lib/DeploymentContext";

const getAlignmentPillars = (d) => [
  {
    title: "Opportunity Zones",
    body: d.federalOzLine,
  },
  {
    title: "Faith-Based Partnerships",
    body:
      "Faith-rooted lineage forged inside the Woodlawn / WCDC tradition, deployed without religious litmus tests. This is the model Charitable Choice was written to enable — and a stated HUD priority under Secretary Turner.",
  },
  {
    title: "Workforce-Housing Fusion",
    body: d.federalConnectorLine,
  },
  {
    title: "Local Empowerment",
    body: d.federalPrimaryRecipient,
  },
  {
    title: "Measurable Accountability",
    body:
      "1:38 Service Connector caseload cap, same-day intake, annual independent evaluation published as public record. Agencies that underperform lose their contracts via re-bid. HUD gets a program it can audit on demand.",
  },
  {
    title: "Replicable Win",
    body: d.federalScaleLine,
  },
];

const getFederalAsks = (d) => [
  {
    label: "Endorsement",
    body: "Public HUD endorsement of The Bridge Model as a high-fidelity municipal service-connector framework.",
  },
  {
    label: "CDBG Technical Assistance",
    body: d.federalTaLine,
  },
  {
    label: "OZ Coordination",
    body: "Opportunity Zone coordination for the model's 5-year homeownership-ladder integration.",
  },
  {
    label: "30-Minute Briefing",
    body: "A direct briefing with Secretary Turner and senior staff in Washington.",
  },
];

export default function FederalStrategy() {
  const d = useDeployment();
  const alignmentPillars = getAlignmentPillars(d);
  const federalAsks = getFederalAsks(d);
  return (
    <section
      id="federal"
      data-testid="section-federal-strategy"
      className="bg-[#0A192F] text-[#F9F7F1] py-24 lg:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />
      <div className="absolute -top-20 -right-20 w-[420px] h-[420px] rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#D4AF37]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                Federal Strategy · White House &amp; HUD Coordination
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
              A federally-endorsable win.<br />
              <em className="italic text-[#D4AF37]">Aligned to administration priorities.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#CBD5E1] leading-relaxed">
            <p>
              The Bridge Model is being presented directly to{" "}
              <strong className="text-[#F9F7F1]">the President</strong> and to{" "}
              <strong className="text-[#F9F7F1]">HUD Secretary Scott Turner</strong>{" "}
              as a deployment-ready proposal that satisfies the administration's
              stated priorities in Opportunity Zones, faith-based partnerships,
              and workforce-housing fusion — all while delivering measurable
              outcomes for underserved American families.
            </p>
            <div className="mt-5">
              <SamGovBadge data-testid="federal-sam-badge" />
            </div>
          </div>
        </div>

        {/* 6 alignment pillars */}
        <ul
          data-testid="federal-alignment"
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#F9F7F1]/15 border border-[#F9F7F1]/15 mb-14"
        >
          {alignmentPillars.map((p, i) => (
            <li
              key={p.title}
              data-testid={`federal-pillar-${i}`}
              className="bg-[#0A192F] p-6 lg:p-7"
            >
              <div className="flex items-center justify-between mb-3">
                <Target className="h-5 w-5 text-[#D4AF37]" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37]">
                  0{i + 1}
                </span>
              </div>
              <h3 className="font-display text-base lg:text-lg text-[#F9F7F1] leading-snug mb-2">
                {p.title}
              </h3>
              <p className="text-xs lg:text-sm text-[#CBD5E1] leading-relaxed">
                {p.body}
              </p>
            </li>
          ))}
        </ul>

        {/* Two-column: Recipients + Asks */}
        <div className="grid lg:grid-cols-2 gap-px bg-[#F9F7F1]/15 border border-[#F9F7F1]/15 mb-14">
          <div className="bg-[#0A192F] p-7 lg:p-9">
            <div className="flex items-center gap-3 mb-4">
              <Landmark className="h-6 w-6 text-[#D4AF37]" />
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                Recipients
              </div>
            </div>
            <ul className="space-y-4">
              <li data-testid="recipient-potus">
                <div className="font-display text-lg text-[#F9F7F1]">The President of the United States</div>
                <div className="text-xs text-[#94A3B8] mt-1 font-mono uppercase tracking-wider">The White House  ·  1600 Pennsylvania Ave NW  ·  Washington, D.C.</div>
                <p className="text-sm text-[#CBD5E1] mt-2 leading-relaxed">
                  Endorsement of a faith-rooted, work-first, locally-empowered
                  program ready to deploy in underserved American communities.
                </p>
              </li>
              <li data-testid="recipient-secretary" className="pt-4 border-t border-[#F9F7F1]/10">
                <div className="font-display text-lg text-[#F9F7F1]">Secretary Scott Turner</div>
                <div className="text-xs text-[#94A3B8] mt-1 font-mono uppercase tracking-wider">U.S. Department of Housing & Urban Development</div>
                <p className="text-sm text-[#CBD5E1] mt-2 leading-relaxed">
                  Direct coordination on Opportunity Zones, faith-based
                  partnerships, and workforce-housing fusion — Secretary
                  Turner's three signature priorities at HUD.
                </p>
              </li>
            </ul>
          </div>

          <div className="bg-[#0A192F] p-7 lg:p-9">
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck className="h-6 w-6 text-[#D4AF37]" />
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
                The Federal Ask
              </div>
            </div>
            <ul className="space-y-3.5">
              {federalAsks.map((a, i) => (
                <li
                  key={a.label}
                  data-testid={`federal-ask-${i}`}
                  className="flex items-start gap-3"
                >
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mt-1 w-20 shrink-0">
                    0{i + 1}
                  </div>
                  <div>
                    <div className="font-display text-base text-[#F9F7F1]">{a.label}</div>
                    <p className="text-sm text-[#CBD5E1] mt-1 leading-relaxed">{a.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Pitch packet CTA */}
        <div
          data-testid="federal-packet-cta"
          className="bg-[#F9F7F1] text-[#0A192F] p-7 lg:p-10 grid lg:grid-cols-12 gap-6 items-center"
        >
          <div className="lg:col-span-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000] mb-2">
              Federal Pitch Packet
            </div>
            <div className="font-display text-xl lg:text-2xl leading-snug mb-2">
              Download the 3-document briefing for The White House &amp; HUD.
            </div>
            <p className="text-sm text-[#475569] leading-relaxed max-w-2xl">
              A confidential federal-coordination packet: (01) letter to the
              President, (02) substantive letter to Secretary Turner, (03)
              two-page Executive Briefing covering the model, the numbers, the
              alignment, and the ask. Print-ready on letterhead or send
              digitally.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <button
              type="button"
              data-testid="download-federal-packet"
              onClick={generateFederalPitchPacket}
              className="inline-flex items-center justify-center gap-2 bg-[#0A192F] hover:bg-[#1F2937] text-[#F9F7F1] h-12 px-6 font-mono text-xs uppercase tracking-widest transition-colors"
            >
              <FileDown className="h-4 w-4" /> Download Packet (PDF) <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
